// src/App.js
import React, { Suspense, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from './components/Header';
import About from './components/About';
import Footer from './components/Footer';
import Roadmap from './components/Roadmap';
import { SkeletonGrid } from './components/ui/skeleton';
import './index.css';

const Experience = React.lazy(() => import('./components/Experience'));
const Projects = React.lazy(() => import('./components/Projects'));
const Skills = React.lazy(() => import('./components/Skills'));
const Education = React.lazy(() => import('./components/Education'));
const Certifications = React.lazy(() => import('./components/Certifications'));
const Contact = React.lazy(() => import('./components/Contact'));

function App() {
  const [showRoadmap, setShowRoadmap] = useState(false);

  
  useEffect(() => {
    let clickCount = 0;
    let clickTimer = null;

    const handleKeyPress = (e) => {
      if (e.key.toLowerCase() === 'l') {
        clickCount++;
        console.log('L pressed:', clickCount); // Debug log
        
        // Reset counter after 2 seconds if no new L press
        clearTimeout(clickTimer);
        clickTimer = setTimeout(() => {
          clickCount = 0;
        }, 2000);

        if (clickCount === 3) {
          setShowRoadmap(prev => !prev);
          console.log('Roadmap toggled!'); // Debug log
          clickCount = 0;
        }
      }
    };

    // Listen for custom event from Header (mobile clicks)
    const handleRoadmapEvent = () => {
      setShowRoadmap(prev => !prev);
      console.log('Roadmap toggled via mobile click!');
    };

    window.addEventListener('keydown', handleKeyPress);
    window.addEventListener('openRoadmap', handleRoadmapEvent);
    
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      window.removeEventListener('openRoadmap', handleRoadmapEvent);
      clearTimeout(clickTimer);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <title>Anguluri Narasimha - Java Backend Developer</title>

        <meta
          name="description"
          content="Portfolio of Anguluri Narasimha, a Java Backend Developer skilled in Spring Boot, REST APIs, PostgreSQL, MySQL, Docker, AWS S3, and secure authentication systems."
        />

        <meta
          name="keywords"
          content="Narasimha, Anguluri Narasimha, Java Developer, Spring Boot Developer, Backend Developer, Portfolio, REST APIs, Spring Security, JWT"
        />

        <meta name="author" content="Anguluri Narasimha" />

        {/* Open Graph SEO */}
        <meta property="og:title" content="Anguluri Narasimha - Portfolio" />
        <meta
          property="og:description"
          content="Explore projects, skills, professional experience, AI work, and contact details of Java Backend Developer Anguluri Narasimha."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://narasimhaanguluri.in" />

        {/* Twitter SEO */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Anguluri Narasimha - Portfolio" />
        <meta
          name="twitter:description"
          content="Explore my projects, skills, and experience in backend engineering and AI."
        />

        {/* Canonical URL */}
        <link rel="canonical" href="https://narasimhaanguluri.in" />

        {/* Structured Data */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Person",
            "name": "Anguluri Narasimha",
            "url": "https://narasimhaanguluri.in",
            "sameAs": [
              "https://github.com/Narasimha123001",
              "https://www.linkedin.com/in/narasimha-anguluri-1b72a12a6"
            ],
            "jobTitle": "Java Backend Developer",
            "description": "Portfolio website of Anguluri Narasimha, showcasing backend development projects, skills, certifications and achievements."
          }
        `}</script>

        {/* SEO Robots */}
        <meta name="robots" content="index, follow" />
      </Helmet>

      <Header />

      <div className="main-content">
        <About />
        <Suspense fallback={<div className="container py-16"><SkeletonGrid count={6} /></div>}>
          <Experience />
          <Projects />
          <Skills />
          <Education />
          <Certifications />
          <Contact />
        </Suspense>
      </div>

      {/* Roadmap Modal/Page Overlay */}
      {showRoadmap && (
        <Roadmap onClose={() => setShowRoadmap(false)} />
      )}

      <Footer />
    </div>
  );
}

export default App;
